import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES, ROLE_DASHBOARDS } from "@/constants/roles";
import OptimizedImage from "@/components/shared/OptimizedImage";

const ROLE_MENUS: Record<string, Array<{ label: string; path: string }>> = {
  [ROLES.ADMIN]: [
    { label: 'Tổng Quan', path: '/admin' },
    { label: 'Người Dùng', path: '/admin-settings' },
    { label: 'Bất Động Sản', path: '/properties' },
    { label: 'Cài Đặt', path: '/admin-settings' },
  ],
  [ROLES.LANDLORD]: [
    { label: 'Bảng Điều Khiển', path: '/landlord' },
    { label: 'Bất Động Sản', path: '/list-property' },
    { label: 'Đơn Thuê', path: '/landlord' },
    { label: 'Giao Dịch', path: '/landlord' },
  ],
  [ROLES.TENANT]: [
    { label: 'Bảng Điều Khiển', path: '/tenant' },
    { label: 'Đơn Thuê Của Tôi', path: '/tenant' },
    { label: 'Thanh Toán', path: '/tenant' },
    { label: 'Báo Cáo Sửa Chữa', path: '/tenant' },
  ],
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, signOut, userRole } = useAuth();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    // passive: true tells the browser we won't call preventDefault(),
    // letting it optimize scroll handling (avoids scroll jank).
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on Escape (a11y: keyboard users expect ESC to dismiss menus)
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const dashboardPath = userRole ? ROLE_DASHBOARDS[userRole] : null;
  const roleMenu = userRole ? ROLE_MENUS[userRole] : null;

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-background shadow-md py-3' : 'bg-transparent py-5'
    }`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Link to="/" className="flex items-center text-2xl font-bold theme-primary-text gap-1">
          <OptimizedImage
            src="/lovable-uploads/7b314dd5-c766-4046-8e2f-02a35e9e67c2.png"
            alt="Propico Logo"
            className="h-8 w-12"
            width={48}
            height={32}
            priority
          />
          Propico
        </Link>
        
        <div className="hidden md:flex items-center space-x-8">
          {user && roleMenu ? (
            <>
              {roleMenu.map((item) => (
                <Link
                  key={item.path + item.label}
                  to={item.path}
                  className="text-foreground/80 hover:text-primary transition-colors font-medium text-sm"
                >
                  {item.label}
                </Link>
              ))}
              <Button onClick={signOut} variant="outline" size="sm">
                <LogOut className="mr-2 h-4 w-4" />
                Đăng xuất
              </Button>
            </>
          ) : (
            <>
              <NavLink to="/#guarantee" onClick={() => scrollToSection('triple-guarantee')}>Cam Kết</NavLink>
              <NavLink to="/#how-it-works" onClick={() => scrollToSection('how-it-works')}>Cách Hoạt Động</NavLink>
              <NavLink to="/#packages" onClick={() => scrollToSection('packages')}>Bảng Giá</NavLink>
              <NavLink to="/rent-predictor">Ước Tính Giá</NavLink>
              <NavLink to="/#faq" onClick={() => scrollToSection('faq')}>Câu Hỏi</NavLink>
              <Link to="/auth">
                <Button variant="default" size="sm">
                  <User className="mr-2 h-4 w-4" />
                  Đăng nhập
                </Button>
              </Link>
            </>
          )}
        </div>
        
        <button
          className="md:hidden text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMobileMenuOpen ? 'Đóng menu' : 'Mở menu'}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      
      <div
        id="mobile-menu"
        className={`md:hidden absolute w-full bg-background transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-screen shadow-md' : 'max-h-0 overflow-hidden'
        }`}
      >
        <div className="container mx-auto px-4 py-4 space-y-4">
          {user && roleMenu ? (
            <>
              {roleMenu.map((item) => (
                <MobileNavLink
                  key={item.path + item.label}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </MobileNavLink>
              ))}
              <Button 
                onClick={() => {
                  signOut();
                  setIsMobileMenuOpen(false);
                }} 
                variant="outline" 
                className="w-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Đăng xuất
              </Button>
            </>
          ) : (
            <>
              <MobileNavLink 
                to="/#guarantee" 
                onClick={() => {
                  scrollToSection('triple-guarantee');
                  setIsMobileMenuOpen(false);
                }}
              >
                Cam Kết
              </MobileNavLink>
              <MobileNavLink 
                to="/#how-it-works" 
                onClick={() => {
                  scrollToSection('how-it-works');
                  setIsMobileMenuOpen(false);
                }}
              >
                Cách Hoạt Động
              </MobileNavLink>
              <MobileNavLink 
                to="/#packages" 
                onClick={() => {
                  scrollToSection('packages');
                  setIsMobileMenuOpen(false);
                }}
              >
                Bảng Giá
              </MobileNavLink>
              <MobileNavLink 
                to="/#faq" 
                onClick={() => {
                  scrollToSection('faq');
                  setIsMobileMenuOpen(false);
                }}
              >
                Câu Hỏi
              </MobileNavLink>
              <MobileNavLink 
                to="/rent-predictor" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Ước Tính Giá
              </MobileNavLink>
              <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>
                <Button variant="default" className="w-full">
                  <User className="mr-2 h-4 w-4" />
                  Đăng nhập
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

const NavLink = ({ to, onClick, children }: { 
  to: string; 
  onClick?: () => void;
  children: React.ReactNode 
}) => {
  const isHashLink = to.includes('#');
  
  if (isHashLink) {
    return (
      <a 
        href={to} 
        className="text-foreground/80 hover:text-primary transition-colors font-medium"
        onClick={(e) => {
          e.preventDefault();
          if (onClick) onClick();
        }}
      >
        {children}
      </a>
    );
  }
  
  return (
    <Link 
      to={to} 
      className="text-foreground/80 hover:text-primary transition-colors font-medium"
    >
      {children}
    </Link>
  );
};

const MobileNavLink = ({ 
  to, 
  onClick, 
  children 
}: { 
  to: string; 
  onClick: () => void; 
  children: React.ReactNode 
}) => {
  const isHashLink = to.includes('#');
  
  if (isHashLink) {
    return (
      <a 
        href={to} 
        className="block py-2 text-foreground/80 hover:text-primary transition-colors font-medium"
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {children}
      </a>
    );
  }
  
  return (
    <Link 
      to={to} 
      className="block py-2 text-foreground/80 hover:text-primary transition-colors font-medium"
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

export default Navbar;
